import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  Paper,
  Portal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { each, flatten, map } from 'lodash'

import { TechnicalCondition } from '../api/index'
import ButtonBox from '../components/ButtonBox'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  ArrowLeft,
  ArrowRight,
  CancelIcon,
  CheckIcon,
  SaveIcon,
} from '../icons'

function ControlsEditUsedCarControl() {
  const { controlId, portalRef, onCancelClick } = useOutletContext()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const [counter, setCounter] = useState(0)
  const [values, setValues] = useState([])
  const [isBtnLoading, setIsBtnLoading] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    TechnicalCondition.getTechnicalCondition(axiosPrivate, controlId)
      .then(({ data }) => {
        setValues(data)
      })
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, controlId])

  useEffect(() => {
    let newCounter = 0
    each(values.getTechnicalConditionGroupModel, (group) =>
      each(group.technicalChecklistGroupElements, (elem) => {
        if (elem.technicalConditionStatus === 'NotOk') {
          newCounter += 1
        }
      }),
    )
    setCounter(newCounter)
  }, [values])

  const saveTechnicalConditions = (redirect) => (e) => {
    e.preventDefault()
    setIsBtnLoading(true)
    const payload = flatten(
      map(values.getTechnicalConditionGroupModel, (group) =>
        map(
          group.technicalChecklistGroupElements,
          ({
            technicalChecklistItemId,
            technicalConditionStatus,
            description,
          }) => ({
            technicalChecklistItemId,
            technicalConditionStatus,
            description,
          }),
        ),
      ),
    )

    TechnicalCondition.saveTechnicalCondition(axiosPrivate, controlId, {
      technicalConditions: payload,
      description: values.description,
    })
      .then(() => {
        if (redirect) {
          navigate(redirect)
        }
      })
      // eslint-disable-next-line no-console
      .catch((er) => console.error(er))
      .finally(() => {
        setIsBtnLoading(false)
      })
  }

  const handleChange = (item) => (e) => {
    e.preventDefault()
    setValues({
      ...values,
      getTechnicalConditionGroupModel: map(
        values.getTechnicalConditionGroupModel,
        (group) => {
          if (
            group.technicalChecklistGroupId === item.technicalChecklistGroupId
          ) {
            return {
              ...group,
              technicalChecklistGroupElements: map(
                group.technicalChecklistGroupElements,
                (elem) => {
                  if (
                    elem.technicalChecklistItemId ===
                    item.technicalChecklistItemId
                  ) {
                    return { ...elem, [e.target.name]: e.target.value }
                  }
                  return elem
                },
              ),
            }
          }
          return group
        },
      ),
    })
  }

  const handleDescChange = (e) => {
    e.preventDefault()

    setValues({ ...values, [e.target.name]: e.target.value })
  }

  return (
    <div>
      <Section>
        <SectionHeader
          icon={<CheckIcon />}
          title={t('controlsEditTodoTitle')}
          subtitle={t('controlsEditTodoSubTitle')}
        />
        <Box>
          <Paper elevation={0} sx={{ p: 5 }}>
            {counter ? (
              <List disablePadding>
                {map(values.getTechnicalConditionGroupModel, (group) =>
                  map(
                    group.technicalChecklistGroupElements,
                    (elem, key) =>
                      elem.technicalConditionStatus === 'NotOk' && (
                        <ListItem
                          key={key}
                          alignItems="flex-start"
                          disablePadding
                          sx={{ mb: 3 }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              flexGrow: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: {
                                  xs: 'flex-start',
                                  md: 'center',
                                },
                                flexDirection: {
                                  xs: 'column',
                                  md: 'row',
                                },
                                flexGrow: 1,
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography variant="body2">
                                {elem.technicalChecklistItemName}
                              </Typography>
                              <FormControl>
                                <RadioGroup
                                  name="technicalConditionStatus"
                                  value={elem.technicalConditionStatus || ''}
                                  onChange={handleChange(elem)}
                                  row
                                  sx={{
                                    flexWrap: 'nowrap',
                                    my: {
                                      xs: 2,
                                      md: 0,
                                    },
                                  }}
                                >
                                  <FormControlLabel
                                    value="Ok"
                                    control={<Radio />}
                                    label="Dobrze"
                                    labelPlacement="top"
                                    componentsProps={{
                                      typography: { variant: 'caption' },
                                    }}
                                  />
                                  <FormControlLabel
                                    value="NotOk"
                                    control={<Radio />}
                                    label="Źle"
                                    labelPlacement="top"
                                    componentsProps={{
                                      typography: { variant: 'caption' },
                                    }}
                                  />
                                </RadioGroup>
                              </FormControl>
                            </Box>
                            {elem.technicalConditionStatus === 'NotOk' && (
                              <TextField
                                name="description"
                                value={elem.description}
                                onChange={handleChange(elem)}
                              />
                            )}
                          </Box>
                        </ListItem>
                      ),
                  ),
                )}
              </List>
            ) : (
              <Typography sx={{ mb: 3 }}>
                {t('controlsEditNoRepairs')}
              </Typography>
            )}

            <Divider sx={{ mb: 3 }} />
            <Typography variant="h4" component="p" gutterBottom>
              {t('carControl.description')}
            </Typography>
            <TextField
              name="description"
              value={values.description || ''}
              fullWidth
              multiline
              minRows={2}
              maxRows={10}
              onChange={handleDescChange}
            />
          </Paper>
        </Box>
        <Portal container={portalRef.current}>
          <ButtonBox
            buttons={[
              {
                label: 'Wstecz',
                icon: <ArrowLeft fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTechnicalConditions(
                    `/controls/${controlId}/edit/technicalCondition`,
                  ),
                },
              },
              {
                label: t('backToListBtnLabel'),
                icon: <CancelIcon />,
                props: {
                  variant: 'outlined',
                  color: 'primary',
                  onClick: onCancelClick,
                },
              },
              {
                label: 'Zapisz',
                icon: <SaveIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTechnicalConditions(false),
                },
              },
              {
                label: 'Zakończ',
                icon: <CheckIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTechnicalConditions(
                    `/controls/${controlId}/summary`,
                  ),
                },
              },
              {
                label: t('nextBtnLabel'),
                icon: <ArrowRight fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: isBtnLoading,
                  onClick: saveTechnicalConditions(
                    `/controls/${controlId}/summary`,
                  ),
                },
              },
            ]}
          />
        </Portal>
      </Section>
    </div>
  )
}

export default ControlsEditUsedCarControl
